/**
 * The assistant's data in the browser. The list, settings and pending steps are
 * state.ext.assistant (GET /api/state, then SSE 'ext' type 'state'); a whole conversation comes
 * from GET /api/assistant/conversations/:id and SSE 'ext' type 'conversation', kept in a small
 * cache here. Every change goes through the /api/assistant routes and its answer updates both.
 * Demo mode (?mock=1) runs the same calls on demo data (mock.ts).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ASSISTANT_EXT, summarizeConversation } from '@orgus/protocol';
import type { AssistantLive, AssistantLiveEvent, AssistantSettings, AssistantSettingsPatch, AssistantState, Conversation, ConversationPatch, Id } from '@orgus/protocol';
import { ApiError, api, apiDelete, apiGet, apiPost, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { onExtEvent, useOrgus } from '../../lib/store.ts';
import { mockConversations, mockDecide, mockReply, mockSend, mockSettings, mockStart, mockStop, stateOf } from './mock.ts';

const POLL_MS = 5_000;
/** How long the demo takes to answer. */
const MOCK_REPLY_MS = 1_600;

// ---------------------------------------------------------------------------
// Conversation cache
// ---------------------------------------------------------------------------

interface CacheStore {
  byId: Record<Id, Conversation>;
  put: (c: Conversation) => void;
  drop: (id: Id) => void;
  clear: () => void;
}

export const useConversationCache = create<CacheStore>()((set) => ({
  byId: {},
  put: (c) =>
    set((st) => {
      const prev = st.byId[c.id];
      // An older copy (a slow GET racing a live event) never replaces a newer one.
      if (prev && prev.updatedAt > c.updatedAt) return st;
      return { byId: { ...st.byId, [c.id]: c } };
    }),
  drop: (id) =>
    set((st) => {
      const byId = { ...st.byId };
      delete byId[id];
      return { byId };
    }),
  clear: () => set({ byId: {} }),
}));

/** What each reply in flight is writing right now (SSE 'live'), by conversation. Never stored. */
interface LiveStore {
  byId: Record<Id, AssistantLive | null>;
  set: (id: Id, live: AssistantLive | null) => void;
}

export const useLiveStore = create<LiveStore>()((set) => ({
  byId: {},
  set: (id, live) => set((st) => ({ byId: { ...st.byId, [id]: live } })),
}));

/** The words and thinking a reply in flight has written so far, or null. */
export function useLive(id: Id | null): AssistantLive | null {
  const fromStream = useLiveStore((s) => (id ? (s.byId[id] ?? null) : null));
  const fromCopy = useConversationCache((s) => (id ? (s.byId[id]?.live ?? null) : null));
  // A page opened mid-reply has the copy the GET returned until the stream says more.
  if (fromStream && (!fromCopy || fromStream.at >= fromCopy.at || fromStream.runId !== fromCopy.runId)) return fromStream;
  return fromCopy;
}

// The hub sends the whole conversation whenever one changes, and what a reply writes as it goes.
onExtEvent((e) => {
  if (e.ext !== ASSISTANT_EXT) return;
  if (e.type === 'conversation') {
    const c = e.data as Conversation | null;
    if (c && typeof c === 'object' && typeof c.id === 'string' && Array.isArray(c.entries)) useConversationCache.getState().put(c);
  } else if (e.type === 'live') {
    const d = e.data as AssistantLiveEvent | null;
    if (d && typeof d.conversationId === 'string') useLiveStore.getState().set(d.conversationId, d.live ?? null);
  }
});

const isMock = () => useOrgus.getState().mode === 'mock';
const slice = (): AssistantState | undefined => useOrgus.getState().ext[ASSISTANT_EXT] as AssistantState | undefined;
const setSlice = (s: AssistantState) => useOrgus.getState().applyExt({ ext: ASSISTANT_EXT, type: 'state', data: s });

/** Put a conversation the hub answered with into the cache and its summary into the list. */
function accept(c: Conversation): Conversation {
  useConversationCache.getState().put(c);
  const cur = slice();
  if (cur) {
    const sum = summarizeConversation(c);
    const rest = cur.conversations.filter((x) => x.id !== c.id);
    const pending = cur.pending.filter((p) => p.conversationId !== c.id);
    for (const en of c.entries) if (en.step?.outcome === 'pending') pending.push({ conversationId: c.id, conversationTitle: c.title, entryId: en.id, step: en.step, at: en.at });
    setSlice({ ...cur, conversations: [sum, ...rest].sort((a, b) => b.updatedAt - a.updatedAt), pending: pending.sort((a, b) => a.at - b.at) });
  }
  return c;
}

// ---------------------------------------------------------------------------
// Demo mode
// ---------------------------------------------------------------------------

function demoMachines() {
  return Object.values(useOrgus.getState().machines)
    .filter((m) => m.online)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((m) => ({ id: m.id, name: m.name }));
}

function seedMock(): void {
  if (slice()) return;
  const now = Date.now();
  const convs = mockConversations(now, demoMachines());
  for (const c of convs) useConversationCache.getState().put(c);
  setSlice(stateOf(mockSettings(), convs));
}

const GONE: UiError = { what: 'That conversation is gone.', why: 'It was deleted, or the demo data was reset.', next: 'Pick another conversation or start a new one.', status: 404 };

function mockConversation(id: Id): Conversation {
  const c = useConversationCache.getState().byId[id];
  if (!c) throw new ApiError(GONE, 404);
  return c;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

function scheduleMockReply(id: Id): void {
  setTimeout(() => {
    const c = useConversationCache.getState().byId[id];
    if (c && c.state === 'running') accept(mockReply(c, Date.now()));
  }, MOCK_REPLY_MS);
}

// ---------------------------------------------------------------------------
// The state slice
// ---------------------------------------------------------------------------

export type AssistantPhase = 'loading' | 'ready' | 'missing' | 'error';

export interface AssistantView {
  phase: AssistantPhase;
  state: AssistantState | null;
  error: UiError | null;
  retry: () => Promise<void>;
}

/**
 * state.ext.assistant with its loading, missing and error states. When the first state came
 * without it (an older hub, or a hub that sends it only on change), it reads GET /api/assistant
 * and keeps reading every few seconds while the page is visible.
 */
export function useAssistant(): AssistantView {
  const { phase: storePhase, mode, state } = useOrgus(
    useShallow((s) => ({ phase: s.phase, mode: s.mode, state: (s.ext[ASSISTANT_EXT] as AssistantState | undefined) ?? null })),
  );
  const [error, setError] = useState<UiError | null>(null);
  const [missing, setMissing] = useState(false);
  const mock = mode === 'mock';
  const ready = storePhase === 'ready';
  // Whether this hub streams the slice: it was there before any GET of ours.
  const streamed = useRef<boolean | null>(null);
  if (ready && streamed.current === null) streamed.current = !!state;

  const fetchOnce = useCallback(async () => {
    try {
      setSlice(await apiGet<AssistantState>('/api/assistant'));
      setError(null);
      setMissing(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true);
      else setError(toUiError(e));
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (mock) {
      seedMock();
      return;
    }
    if (streamed.current) return;
    void fetchOnce();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void fetchOnce();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [ready, mock, fetchOnce]);

  const retry = useCallback(async () => {
    setError(null);
    await fetchOnce();
  }, [fetchOnce]);

  if (state) return { phase: 'ready', state, error, retry };
  if (storePhase === 'error') return { phase: 'error', state, error: useOrgus.getState().loadError ?? error, retry };
  if (missing) return { phase: 'missing', state, error: null, retry };
  if (error) return { phase: 'error', state, error, retry };
  return { phase: 'loading', state, error: null, retry };
}

/** Steps waiting for a person across conversations (cheap selector for Needs you and the Inbox). */
export function useAssistantPending(): AssistantState['pending'] {
  const mode = useOrgus((s) => s.mode);
  const ready = useOrgus((s) => s.phase === 'ready');
  useEffect(() => {
    if (mode === 'mock' && ready) seedMock();
  }, [mode, ready]);
  return useOrgus((s) => (s.ext[ASSISTANT_EXT] as AssistantState | undefined)?.pending ?? EMPTY);
}
const EMPTY: AssistantState['pending'] = [];

// ---------------------------------------------------------------------------
// One conversation
// ---------------------------------------------------------------------------

export interface ConversationView {
  conversation: Conversation | null;
  phase: 'idle' | 'loading' | 'ready' | 'error';
  error: UiError | null;
  retry: () => Promise<void>;
}

/**
 * The whole conversation `id`: from the cache, read again when the list says it changed after
 * the copy we hold (a hub that does not send 'conversation' events still shows every change).
 */
export function useConversation(id: Id | null): ConversationView {
  const conversation = useConversationCache((s) => (id ? (s.byId[id] ?? null) : null));
  const listedAt = useOrgus((s) => (id ? (s.ext[ASSISTANT_EXT] as AssistantState | undefined)?.conversations.find((c) => c.id === id)?.updatedAt : undefined));
  const mock = useOrgus((s) => s.mode === 'mock');
  const [error, setError] = useState<UiError | null>(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef<string | null>(null);

  const fetchOnce = useCallback(async (cid: Id) => {
    if (inFlight.current === cid) return;
    inFlight.current = cid;
    setLoading(true);
    try {
      useConversationCache.getState().put(await apiGet<Conversation>(`/api/assistant/conversations/${encodeURIComponent(cid)}`));
      setError(null);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      if (inFlight.current === cid) inFlight.current = null;
      setLoading(false);
    }
  }, []);

  useEffect(() => setError(null), [id]);

  const stale = !!id && !mock && (!conversation || (listedAt != null && listedAt > conversation.updatedAt));
  useEffect(() => {
    if (id && stale) void fetchOnce(id);
  }, [id, stale, fetchOnce]);

  const retry = useCallback(async () => {
    if (id) await fetchOnce(id);
  }, [id, fetchOnce]);

  if (!id) return { conversation: null, phase: 'idle', error: null, retry };
  if (error) return { conversation, phase: 'error', error, retry };
  if (!conversation) return mock && !loading ? { conversation: null, phase: 'error', error: GONE, retry } : { conversation: null, phase: 'loading', error: null, retry };
  return { conversation, phase: 'ready', error: null, retry };
}

// ---------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------

const path = (id: Id) => `/api/assistant/conversations/${encodeURIComponent(id)}`;

/** Start a conversation with its first message; resolves once the hub has it (the reply runs on). */
export async function startConversation(text: string): Promise<Conversation> {
  if (isMock()) {
    await wait(250);
    const st = slice();
    const c = accept(mockStart(text, Date.now(), st?.settings ?? mockSettings(), demoMachines()[0]?.id ?? null));
    scheduleMockReply(c.id);
    return c;
  }
  return accept(await apiPost<Conversation>('/api/assistant/conversations', { text }));
}

export async function sendMessage(id: Id, text: string): Promise<Conversation> {
  if (isMock()) {
    await wait(250);
    const c = accept(mockSend(mockConversation(id), text, Date.now()));
    scheduleMockReply(c.id);
    return c;
  }
  return accept(await apiPost<Conversation>(`${path(id)}/messages`, { text }));
}

export async function stopReply(id: Id): Promise<Conversation> {
  if (isMock()) {
    await wait(250);
    return accept(mockStop(mockConversation(id), Date.now()));
  }
  return accept(await apiPost<Conversation>(`${path(id)}/stop`));
}

export async function deleteConversation(id: Id): Promise<void> {
  if (!isMock()) await apiDelete<{ ok: true }>(path(id));
  else await wait(200);
  useConversationCache.getState().drop(id);
  const cur = slice();
  if (cur) setSlice({ ...cur, conversations: cur.conversations.filter((c) => c.id !== id), pending: cur.pending.filter((p) => p.conversationId !== id) });
}

/** Rename or pin a conversation. */
export async function patchConversation(id: Id, patch: ConversationPatch): Promise<Conversation> {
  if (isMock()) {
    await wait(150);
    const c = mockConversation(id);
    return accept({ ...c, ...(patch.title !== undefined ? { title: patch.title.replace(/\s+/g, ' ').trim() || c.title } : {}), ...(patch.pinned !== undefined ? { pinned: patch.pinned } : {}) });
  }
  return accept(await api<Conversation>(path(id), { method: 'PATCH', body: patch }));
}

/** Approve or deny a step that waits for a person. */
export async function decideStep(conversationId: Id, approvalId: Id, answer: 'allow' | 'deny', note?: string): Promise<Conversation> {
  const n = note?.trim() || undefined;
  if (isMock()) {
    await wait(350);
    return accept(mockDecide(mockConversation(conversationId), approvalId, answer, n, Date.now()));
  }
  return accept(await apiPost<Conversation>(`/api/assistant/approvals/${encodeURIComponent(approvalId)}`, { answer, ...(n ? { note: n } : {}) }));
}

/** Change settings (owner or admin). Resolves with the settings the hub kept. */
export async function saveAssistantSettings(patch: AssistantSettingsPatch): Promise<AssistantSettings> {
  let saved: AssistantSettings;
  if (isMock()) {
    await wait(300);
    saved = { ...(slice()?.settings ?? mockSettings()), ...patch };
  } else {
    saved = await api<AssistantSettings>('/api/assistant/settings', { method: 'PATCH', body: patch });
  }
  const cur = slice();
  if (cur) setSlice({ ...cur, settings: saved });
  return saved;
}
